import React, { useState, useEffect } from 'react'
import Base from '../Components/Base'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import StarIcon from '@mui/icons-material/Star'
import PersonIcon from '@mui/icons-material/Person'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts'
import { useCookies } from 'react-cookie'
import { useAuth } from '../hooks/useAuth'
import { apiUrl } from '../Services/api'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042']

export default function StatisticsDashboard() {
  const theme = useTheme()
  const { deleteTokenCookie } = useAuth()
  const [cookies] = useCookies(['token'])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchStatistics = () => {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', apiUrl('/statistics/get/my-statistics'), true)
      xhr.setRequestHeader('Authorization', 'Bearer ' + cookies.token)

      xhr.onload = () => {
        if (xhr.status === 401) {
          deleteTokenCookie()
          return
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const responseData = JSON.parse(xhr.responseText)
            setData(responseData)
          } catch (e) {
            setError('Erreur lors du traitement des données.')
          }
        } else {
          setError(`Erreur ${xhr.status}: Impossible de récupérer les statistiques.`)
        }
        setLoading(false)
      }

      xhr.onerror = () => {
        setError('Une erreur réseau est survenue.')
        setLoading(false)
      }

      xhr.send()
    }

    fetchStatistics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookies.token])

  if (loading) {
    return (
      <Base>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      </Base>
    )
  }

  if (error || !data) {
    return (
      <Base>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <Alert severity="error">{error || "Erreur de chargement des statistiques."}</Alert>
        </Box>
      </Base>
    )
  }

  const { kpiData, monthlyData, genreData, topAuthors } = data

  return (
    <Base>
      <Box>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 4, color: 'text.primary' }}>
          Tableau de bord des statistiques
        </Typography>

        {/* KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* KPI 1 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.light, mr: 2 }}>
                    <MenuBookIcon color="primary" />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary">
                    Objectif Annuel
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                  {kpiData.booksReadThisYear} / {kpiData.annualGoal}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(kpiData.booksReadThisYear / kpiData.annualGoal) * 100}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {Math.min(100, Math.round((kpiData.booksReadThisYear / kpiData.annualGoal) * 100))}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* KPI 2 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.success.light, mr: 2 }}>
                    <TrendingUpIcon color="success" />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary">
                    Pages Lues
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {kpiData.totalPagesRead.toLocaleString('fr-FR')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* KPI 3 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.warning.light, mr: 2 }}>
                    <AccessTimeIcon color="warning" />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary">
                    Temps Moyen
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {kpiData.avgReadingTime} <Typography component="span" variant="h6">jours</Typography>
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* KPI 4 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.info.light, mr: 2 }}>
                    <StarIcon color="info" />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary">
                    Note Moyenne
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {kpiData.avgRating} <Typography component="span" variant="h6">/ 5</Typography>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts & Top Authors */}
        <Grid container spacing={3}>
          {/* Bar Chart */}
          <Grid item xs={12} md={8}>
            <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                  Livres lus par mois
                </Typography>
                <Box sx={{ width: '100%', height: 350 }}>
                  <ResponsiveContainer>
                    <BarChart data={monthlyData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: 'none', boxShadow: theme.shadows[3] }}
                        cursor={{ fill: 'transparent' }}
                      />
                      <Bar dataKey="Livres" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} barSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Pie Chart & Top Authors Column */}
          <Grid item xs={12} md={4}>
            <Grid container spacing={3} direction="column">
              {/* Pie Chart */}
              <Grid item xs={12}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                      Répartition des genres
                    </Typography>
                    <Box sx={{ width: '100%', height: 250 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={genreData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {genreData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: theme.shadows[3] }} />
                          <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Top Authors */}
              <Grid item xs={12}>
                <Card elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                      Top Auteurs
                    </Typography>
                    <List disablePadding>
                      {topAuthors.map((author, index) => (
                        <ListItem key={author.id} disableGutters sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={author.name}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {author.count} livres
                          </Typography>
                        </ListItem>
                      ))}
                      {topAuthors.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          Aucun auteur à afficher pour le moment.
                        </Typography>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Base>
  )
}
